import React, {
  startTransition,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { ModalsProvider } from "@mantine/modals";
import { AxiosInterceptor } from "components/AxiosInterceptor";
import { EnterNewPasswordModal } from "containers/UI/Modals/EnterNewPasswordModal";
import { ErrorModal } from "containers/UI/Modals/ErrorModal";
import { lazyModal } from "containers/UI/Modals/lazyModal";
import { ModalWithTopIcon } from "containers/UI/Modals/ModalWithTopIcon";
import { ResetPasswordModal } from "containers/UI/Modals/ResetPasswordModal";
import { VerifyEmailModal } from "containers/UI/Modals/VerifyEmailModal";
import { LazyWelcomeModal } from "containers/UI/Modals/LazyWelcomeModal";
import { Auth, AuthProvider, PickedAuthInstance } from "stores/useAuth";
import { CesiumContextProvider } from "stores/useCesiumContext";
import { EditModeProvider } from "stores/useEditMode";
import { MapContextProvider } from "stores/useMapContext";
import { PublicTracksDataProvider } from "stores/usePublicTracks";
import { TrackStatsProvider } from "stores/useTrackStats";
import { UserTracksDataProvider } from "stores/useUserTracks";
import { UserWaypointDataProvider } from "stores/useUserWaypoints";
import {
  getCacheSizePercentage,
  purgeCache,
} from "lib/leaflet-cached-tile-layer";
import { GenericContextProvider } from "stores/genericStore/GenericContext";

import { MainContainer } from "./containers/MainContainer";
import { OptionsProvider } from "./stores/optionsStore/OptionsContext";
import { RoutingProvider } from "./stores/routingStore/RoutingContext";
import { optionsReducer } from "./stores/optionsStore/reducer";
import { initialOptionsState } from "./stores/optionsStore/state";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { LoadingScreen } from "./LoadingScreen";
import { useTranslation } from "react-i18next";
import { useImageUpload } from "hooks/useImageUpload";
import { MobileMenuProvider } from "stores/useMobileMenu";
import { loadSignedInUserData } from "utils/auth/loadSignedInUserData";
import { useMantineColorScheme } from "@mantine/core";

// Modals opened by user interaction are loaded on demand to keep them out of the main bundle
const ConfirmLosingTrackModal = lazyModal(() =>
  import("containers/UI/Modals/ConfirmLosingTrackModal").then(
    (module) => module.ConfirmLosingTrackModal
  )
);
const CoordsInputModal = lazyModal(() =>
  import("containers/UI/Modals/CoordsInputModal").then(
    (module) => module.CoordsInputModal
  )
);
const EditTrackModal = lazyModal(() =>
  import("containers/UI/Modals/EditTrackModal").then(
    (module) => module.EditTrackModal
  )
);
const EditWaypointModal = lazyModal(() =>
  import("containers/UI/Modals/EditWaypointModal").then(
    (module) => module.EditWaypointModal
  )
);
const GPXViewerAppsModal = lazyModal(() =>
  import("containers/UI/Modals/GPXViewerAppsModal").then(
    (module) => module.GPXViewerAppsModal
  )
);
const ImportGPXModal = lazyModal(() =>
  import("containers/UI/Modals/ImportGPXModal").then(
    (module) => module.ImportGPXModal
  )
);
const PremiumFeatureModal = lazyModal(() =>
  import("containers/UI/Modals/PremiumFeatureModal").then(
    (module) => module.PremiumFeatureModal
  )
);
const ResetEmailSentModal = lazyModal(() =>
  import("containers/UI/Modals/ResetEmailSentModal").then(
    (module) => module.ResetEmailSentModal
  )
);
const SettingsModal = lazyModal(() =>
  import("containers/UI/Modals/SettingsModal").then(
    (module) => module.SettingsModal
  )
);
const SignUpModal = lazyModal(() =>
  import("containers/UI/Modals/SignUpModal").then(
    (module) => module.SignUpModal
  )
);

export const App = () => {
  const userRef = useRef<Auth["user"] | undefined>(undefined);
  const { enableLinkTracking } = useMatomo();
  const { setColorScheme } = useMantineColorScheme();
  const optionsReducerTuple = useReducer(optionsReducer, initialOptionsState);
  const [optionsState, dispatchOptions] = optionsReducerTuple;
  // Read when the user's settings are loaded, without rerunning handleAuth on every options change
  const optionsStateRef = useRef(optionsState);
  useEffect(() => {
    optionsStateRef.current = optionsState;
  }, [optionsState]);
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();
  const { getStorageSize } = useImageUpload();
  const [loading, setLoading] = useState<boolean>(true);
  // The whole app UI replaces the loading screen: rendered as a transition, React renders it in short slices
  // instead of one long task that blocks the page (the loading screen stays until the UI is ready)
  const finishLoading = useCallback(
    () => startTransition(() => setLoading(false)),
    []
  );

  useEffect(() => {
    trackPageView({});
  }, [trackPageView]);

  useEffect(() => {
    getCacheSizePercentage()
      .then((cacheSizePecentage) => {
        const cacheSizePercentageThreshold = 50;
        if (cacheSizePecentage !== null) {
          if (cacheSizePecentage > cacheSizePercentageThreshold) {
            purgeCache(
              import.meta.env.VITE_CACHE_DB_NAME,
              Number(import.meta.env.VITE_CACHE_DB_VERSION)
            )
              .then((success) => {
                if (success) {
                  // console.info("Tile cache purged succesfully!")
                } else {
                  throw new Error(
                    "Tile purge return value didn't return properly as true."
                  );
                }
              })
              .catch((error: DOMException) => {
                console.error("Failed to purge tile cache.", error.message);
              });
          }
        } else {
          throw new Error("Couldn't determine cache size.");
        }
      })
      .catch((error: TypeError) => {
        console.error(
          "Unable to get tile cache size so auto purging won't work.",
          error.message
        );
      });
  }, []);

  const handleAuth = useCallback(
    (authInstance: PickedAuthInstance) => {
      const { user } = authInstance;

      if (!user) {
        finishLoading();
        return;
      }
      // Marked right away: onAuth is called again whenever its dependencies change (e.g. when the settings
      // switch the language or the color scheme), and the requests mustn't be sent again meanwhile
      if (userRef.current) return;
      userRef.current = user;

      loadSignedInUserData({
        auth: { ...authInstance, user },
        dispatchOptions,
        getOptions: () => optionsStateRef.current,
        getStorageSize,
        setColorScheme,
        t,
      })
        .catch((error: Error) => {
          console.error("Initial app requests failed. ", error.message);
        })
        .finally(finishLoading);
    },
    [dispatchOptions, finishLoading, getStorageSize, setColorScheme, t]
  );

  enableLinkTracking();

  return (
    <MobileMenuProvider>
      <MapContextProvider>
        <CesiumContextProvider>
          <TrackStatsProvider>
            <RoutingProvider>
              <AuthProvider
                onAuth={handleAuth}
                onSignOut={() => {
                  userRef.current = undefined;
                  // Unknown like before signing in, not "inactive": the side panel shows the online services
                  // status only when it's known, and the premium features stay locked either way
                  dispatchOptions(
                    updateNonSaveableOptions({
                      allowPremiumFeatures: undefined,
                    })
                  );
                }}
              >
                <AxiosInterceptor>
                  <OptionsProvider reducerTuple={optionsReducerTuple}>
                    <GenericContextProvider>
                      <EditModeProvider>
                        <UserTracksDataProvider>
                          <UserWaypointDataProvider>
                            <PublicTracksDataProvider>
                              <ModalsProvider
                                modals={{
                                  signUp: SignUpModal,
                                  verifyEmail: VerifyEmailModal,
                                  resetPassword: ResetPasswordModal,
                                  resetEmailSent: ResetEmailSentModal,
                                  errorModal: ErrorModal,
                                  editWaypoint: EditWaypointModal,
                                  editTrack: EditTrackModal,
                                  topIconModal: ModalWithTopIcon,
                                  importGPX: ImportGPXModal,
                                  welcome: LazyWelcomeModal,
                                  confirmLosingTrack: ConfirmLosingTrackModal,
                                  coordsInputModal: CoordsInputModal,
                                  settingsModal: SettingsModal,
                                  premiumFeatureModal: PremiumFeatureModal,
                                  gpxViewerAppsModal: GPXViewerAppsModal,
                                  enterNewPasswordModal: EnterNewPasswordModal,
                                }}
                                modalProps={{
                                  centered: true,
                                  // The close button is an icon, this names it
                                  closeButtonProps: {
                                    "aria-label": t("generic.close"),
                                  },
                                  style: (theme) => ({
                                    title: {
                                      fontWeight: 700,
                                      fontSize: theme.fontSizes.lg,
                                    },
                                    root: {
                                      fontSize: theme.fontSizes.sm,
                                    },
                                  }),
                                }}
                              >
                                {loading ? (
                                  <LoadingScreen />
                                ) : (
                                  <MainContainer />
                                )}
                              </ModalsProvider>
                            </PublicTracksDataProvider>
                          </UserWaypointDataProvider>
                        </UserTracksDataProvider>
                      </EditModeProvider>
                    </GenericContextProvider>
                  </OptionsProvider>
                </AxiosInterceptor>
              </AuthProvider>
            </RoutingProvider>
          </TrackStatsProvider>
        </CesiumContextProvider>
      </MapContextProvider>
    </MobileMenuProvider>
  );
};
